import React, { useReducer, useEffect } from "react";
import ReactModal from "react-modal";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";

import {
  MEMBERSHIP_TIER_DEFAULTS,
  DEPOSIT_TIER_OPTIONS,
  BENEFITS_TIER_OPTIONS,
  MEMBERSHIP_PERCENT_OPTIONS,
  formatPercent,
  parsePercentInput,
  isValidMembershipPercent,
  emptyMembership,
} from "../../helpers/membership";

const toastOptions = {
  position: "top-right",
  autoClose: 6000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const snapToHalfPercent = (value) => {
  if (value === null || value === undefined) return null;
  const snapped = Math.round(value / 0.005) * 0.005;
  return +snapped.toFixed(4);
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "tierSelected": {
      const tier = action.value;
      if (tier && MEMBERSHIP_TIER_DEFAULTS[tier]) {
        const d = MEMBERSHIP_TIER_DEFAULTS[tier];
        return {
          ...state,
          benefitsTier: tier,
          activationCreditPercent: d.activationCreditPercent,
          clickToFlyPercent: d.clickToFlyPercent,
          referralBonusPercent: d.referralBonusPercent,
        };
      }
      return { ...state, benefitsTier: tier };
    }
    case "reset":
      return action.value;
    default:
      return state;
  }
};

const buildInitial = (existing) => {
  const base = { ...emptyMembership(), ...(existing || {}) };
  if (!base.activationDate) {
    base.activationDate = new Date();
  } else if (typeof base.activationDate === "string") {
    base.activationDate = new Date(base.activationDate);
  }
  return base;
};

const isComplete = (s) =>
  !!s.activationDate &&
  !!s.depositTier &&
  !!s.benefitsTier &&
  typeof s.activationCreditPercent === "number" &&
  typeof s.clickToFlyPercent === "number" &&
  typeof s.referralBonusPercent === "number" &&
  isValidMembershipPercent(s.activationCreditPercent) &&
  isValidMembershipPercent(s.clickToFlyPercent) &&
  isValidMembershipPercent(s.referralBonusPercent);

const MembershipActivationModal = ({
  isOpen,
  existing,
  onConfirm,
  onCancel,
}) => {
  const [state, dispatch] = useReducer(reducer, buildInitial(existing));

  useEffect(() => {
    if (isOpen) {
      dispatch({ type: "reset", value: buildInitial(existing) });
    }
  }, [isOpen, existing]);

  const handlePercentChange = (field) => (option) => {
    if (!option) {
      dispatch({ type: "fieldChanged", field, value: null });
      return;
    }
    let value = option.value;
    if (option.__isNew__) {
      const parsed = parsePercentInput(option.value);
      if (parsed === null) {
        toast.error("Enter a number between 0 and 10%.", toastOptions);
        return;
      }
      const snapped = snapToHalfPercent(parsed);
      if (
        !isValidMembershipPercent(snapped) ||
        Math.abs(snapped - parsed) > 1e-6
      ) {
        toast.error(
          "Membership % must be 0–10% in 0.5% increments.",
          toastOptions,
        );
        return;
      }
      value = snapped;
    }
    if (!isValidMembershipPercent(value)) {
      toast.error(
        "Membership % must be 0–10% in 0.5% increments.",
        toastOptions,
      );
      return;
    }
    dispatch({ type: "fieldChanged", field, value });
  };

  const renderPercentSelect = (field, label) => (
    <div className="form-group fb-100 ml-0 mt-15">
      <label className="base-label">{label}</label>
      <CreatableSelect
        className="membership-percent-select"
        classNamePrefix="select"
        options={MEMBERSHIP_PERCENT_OPTIONS}
        value={
          state[field] != null
            ? { value: state[field], label: formatPercent(state[field]) }
            : null
        }
        isClearable
        isDisabled={!state.benefitsTier}
        placeholder={state.benefitsTier ? "Select %" : "Pick Benefits Tier first"}
        onChange={handlePercentChange(field)}
        formatCreateLabel={(input) => `Use "${input}"`}
      />
    </div>
  );

  const valid = isComplete(state);

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onCancel}
      style={{
        overlay: {},
        content: { width: "60%", maxWidth: "640px" },
      }}
      appElement={document.getElementById("app")}
    >
      <form
        className="base-form w-100"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) {
            toast.error(
              "All membership fields are required.",
              toastOptions,
            );
            return;
          }
          onConfirm(state);
        }}
      >
        <h3>Activate Membership</h3>
        <p className="text-muted">
          All fields are required. Pick a Benefits Tier to auto-fill the three
          percentages; you can still adjust each individually.
        </p>

        <div className="form-group fb-100 ml-0 mt-15">
          <label className="base-label">Activation Date</label>
          <DatePicker
            className="prospect-date-picker"
            onChange={(date) =>
              dispatch({
                type: "fieldChanged",
                field: "activationDate",
                value: date,
              })
            }
            value={state.activationDate}
            clearIcon={null}
          />
        </div>

        <div className="form-group fb-100 ml-0 mt-15">
          <label className="base-label">Deposit Tier</label>
          <Select
            className="membership-deposit-select"
            classNamePrefix="select"
            options={DEPOSIT_TIER_OPTIONS}
            value={
              DEPOSIT_TIER_OPTIONS.find(
                ({ value }) => value === state.depositTier,
              ) || null
            }
            placeholder="Deposit Tier"
            onChange={(option) =>
              dispatch({
                type: "fieldChanged",
                field: "depositTier",
                value: option?.value || null,
              })
            }
          />
        </div>

        <div className="form-group fb-100 ml-0 mt-15">
          <label className="base-label">Benefits Tier</label>
          <Select
            className="membership-benefits-select"
            classNamePrefix="select"
            options={BENEFITS_TIER_OPTIONS}
            value={
              BENEFITS_TIER_OPTIONS.find(
                ({ value }) => value === state.benefitsTier,
              ) || null
            }
            placeholder="Benefits Tier"
            onChange={(option) =>
              dispatch({ type: "tierSelected", value: option?.value || null })
            }
          />
        </div>

        {renderPercentSelect("activationCreditPercent", "Activation Credit %")}
        {renderPercentSelect("clickToFlyPercent", "Click-to-Fly %")}
        {renderPercentSelect("referralBonusPercent", "Referral Bonus %")}

        <div className="d-flex justify-content-end mt-25">
          <button
            type="button"
            className="base-button evo-grey mr-2"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="base-button evo-blue"
            disabled={!valid}
          >
            Activate Membership
          </button>
        </div>
      </form>
    </ReactModal>
  );
};

export default MembershipActivationModal;
